import {
  type AppBskyActorDefs,
  type AppBskyFeedDefs,
  type BskyAgent,
} from '@atproto/api'

import server from '#/server'
import {attachmentUrl} from '#/server/rice/client'
import {
  type Proposal,
  type ProposalFilter,
  type ProposalRelation,
} from '#/server/rice/types'
import {type FeedAPI} from './types'

type RequestParams = {
  /** 大厅按状态筛;个人页不筛状态,传 'all' */
  state: ProposalFilter
  /** 个人页:看的是「我发起的 / 我参与的」。不传就是公开列表。 */
  relation?: ProposalRelation
}

/**
 * rice 的提案列表是游标分页,core 是 pageNum/pageSize + total。
 * FeedAPI 的 cursor 本来就是不透明字符串,直接把 rice 的游标透传即可 ——
 * 之前那套 `pageIndex * pageSize > total ? '' : pageIndex + 1` 的算术没了。
 */
async function fetchPage(
  agent: BskyAgent,
  params: RequestParams,
  cursor: string | undefined,
  limit: number,
) {
  const {state, relation} = params
  const page = await server.rice.listProposals({
    status: state === 'all' ? undefined : state,
    mine: relation,
    limit,
    before: cursor,
  })
  const feed = page.data.map(restructFeedItem)
  await hydrateAuthors(agent, feed)
  return {
    cursor: page.meta.next_cursor ?? undefined,
    feed,
  }
}

/** app.bsky.actor.getProfiles 一次最多 25 个 actor。 */
const PROFILES_PER_REQUEST = 25

/**
 * 用 appview 的档案补齐发起人。
 *
 * **头像不在 rice 里。** core 的 `t_user.avatar` 存过两种东西:老的 fileId,和
 * PDS 的 blob URL。实际上生产那 2009 个用户存的全是后者 —— 导入后
 * `users.avatar_id` 一个都不为 null,所以 rice 返回的 `author.avatar` 恒为 null,
 * 表现是提案列表的头像全是灰底。改头像的路径也只写 PDS(见
 * `state/queries/profile.ts` 的 `upsertProfile`,以及 Onboarding 里那句
 * 「头像不同步」),所以**头像的权威是 AT Protocol 的 profile 记录**,
 * rice 那一列只是历史遗留。
 *
 * 顺带把 labels / viewer / associated 也带上 —— PostProposalItem 要用它们做
 * 屏蔽判断,rice 拼出来的那个壳里没有。
 *
 * 拿不到就保留 rice 给的那份:头像空着比整个提案列表挂掉好。
 */
async function hydrateAuthors(
  agent: BskyAgent,
  feed: AppBskyFeedDefs.FeedViewPost[],
) {
  const dids = Array.from(
    new Set(feed.map(item => item.post.author.did).filter(Boolean)),
  )
  if (!dids.length) return

  const profiles = new Map<string, AppBskyActorDefs.ProfileViewDetailed>()
  for (let i = 0; i < dids.length; i += PROFILES_PER_REQUEST) {
    try {
      const res = await agent.getProfiles({
        actors: dids.slice(i, i + PROFILES_PER_REQUEST),
      })
      for (const profile of res.data.profiles) {
        profiles.set(profile.did, profile)
      }
    } catch {}
  }

  for (const item of feed) {
    const profile = profiles.get(item.post.author.did)
    if (!profile) continue
    item.post.author = {
      did: profile.did,
      handle: item.post.author.handle,
      // 昵称仍以 rice 为准:提案列表上的「发起方名称」在 core 里就是
      // t_user.nick_name,后台改得动,PDS 的 displayName 改不动。
      displayName: item.post.author.displayName || profile.displayName,
      // 兜底给未来:rice 的 PATCH /api/users/me 能设 avatar_id,真设了就用它
      avatar: profile.avatar ?? item.post.author.avatar,
      associated: profile.associated,
      viewer: profile.viewer,
      labels: profile.labels,
      createdAt: profile.createdAt,
      verification: profile.verification,
      status: profile.status,
    }
  }
}

export class ProposalFeedAPI implements FeedAPI {
  agent: BskyAgent
  params: RequestParams = {state: 'all'}
  constructor({params, agent}: {params: RequestParams; agent: BskyAgent}) {
    this.agent = agent
    this.params = params
  }

  async peekLatest() {
    const {feed} = await fetchPage(this.agent, this.params, undefined, 1)
    return feed[0] ?? null
  }

  async fetch({cursor, limit}: {cursor: string | undefined; limit: number}) {
    return fetchPage(this.agent, this.params, cursor, limit)
  }
}

/**
 * 把 rice 的提案套进 FeedViewPost 的壳里 —— feed 那一层(PostFeedItem 等)
 * 只认 AT Protocol 的形状。rice 自己的字段原样保留,由 PostProposalItem 读。
 */
function restructFeedItem(item: Proposal): AppBskyFeedDefs.FeedViewPost {
  const author = item.author
  return {
    post: {
      ...item,
      cid: item.id,
      author: {
        did: author?.did ?? '',
        displayName: author?.nickname || author?.handle || '',
        avatar: author?.avatar ? attachmentUrl(author.avatar) : undefined,
        handle: author?.handle ?? 'handle.invalid',
      },
      record: {
        $type: 'app.bsky.feed.post',
        text: item.title,
        createdAt: item.inserted_at,
        langs: ['zh'],
      },
      uri: `at://${author?.did ?? ''}/proposal/${item.id}`,
      indexedAt: item.inserted_at,
    },
  }
}
